// LocationSelector.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import '../css/LocationSelector.css';
import LocationAddModal from '../Components/LocationAddModal';

function getRoleFromToken() {
  const token = localStorage.getItem('accessToken');
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.role ?? null; // "ADMIN" | "USER" | "NEW"
  } catch {
    return null;
  }
}

export default function LocationSelector() {
  const [locations, setLocations] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkedNames, setCheckedNames] = useState(new Set()); // ← name 기준으로 변경
  const [newLocation, setNewLocation] = useState({
    name: '',
    lat: null,
    lng: null,
    floorplanUrl: null,
    floorplanFile: null,
  });
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const role = getRoleFromToken();
    setIsAdmin(role === 'ADMIN');
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const res = await api.get('/locations');
      const list = Array.isArray(res.data) ? res.data : [];
      setLocations(list);

      // 목록 갱신 시 존재하지 않는 name은 선택 해제
      setCheckedNames(prev => {
        const existing = new Set(list.map(l => l.name));
        const next = new Set();
        prev.forEach(nm => { if (existing.has(nm)) next.add(nm); });
        return next;
      });
    } catch (err) {
      console.error('위치 목록 로드 실패', err?.response || err);
    }
  };

  const handleModalDone = () => {
    fetchLocations();
    setNewLocation({ name: '', lat: null, lng: null, floorplanUrl: null, floorplanFile: null });
    setShowModal(false);
  };

  const handleClickLocation = (location) => {
    navigate(`/locations/${encodeURIComponent(location.name)}/floors`);
  };

  const toggleChecked = (name) => {
    setCheckedNames(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (checkedNames.size === 0) return alert('삭제할 위치를 선택하세요.');
    if (!window.confirm(`총 ${checkedNames.size}개 위치를 삭제하시겠습니까?\n(하위 층/디바이스 포함)`)) return;
    try {
      // 백엔드가 name으로 삭제를 받는 엔드포인트 여야 합니다.
      for (const name of checkedNames) {
        await api.delete(`/locations/${encodeURIComponent(name)}`); // ← name으로 삭제 호출
      }
      await fetchLocations();
      setCheckedNames(new Set());
    } catch (e) {
      console.error(e);
      alert('일부 삭제에 실패했습니다.');
    }
  };

  return (
    <div className="location-dashboard-container">
      <h1>설치 위치 목록</h1>

      {isAdmin && (
        <div className="location-input-group" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" onClick={() => setShowModal(true)}>위치 추가</button>
          <button
            type="button"
            onClick={handleBulkDelete}
            disabled={checkedNames.size === 0}
            title={checkedNames.size === 0 ? '선택된 항목이 없습니다' : ''}
          >
            선택 삭제
          </button>
        </div>
      )}

      {showModal && (
        <LocationAddModal
          onClose={() => setShowModal(false)}
          onSubmit={handleModalDone}
          newLocation={newLocation}
          setNewLocation={setNewLocation}
        />
      )}

      <table className="location-table">
        <thead>
          <tr>
            <th>위치명</th>
            {isAdmin && <th style={{ width: 80, textAlign: 'center' }}>선택</th>}
          </tr>
        </thead>
        <tbody>
          {locations.map((loc) => (
            <tr key={loc.name}>
              <td onClick={() => handleClickLocation(loc)} style={{ cursor: 'pointer' }}>
                {loc.name}
              </td>
              {isAdmin && (
                <td style={{
        transform: 'scale(1.2)',
        accentColor: '#00eaff',   // 네온 블루
        cursor: 'pointer',
        textAlign:'center'
      }}>
                  <input
                    type="checkbox"
                    checked={checkedNames.has(loc.name)}
                    onChange={() => toggleChecked(loc.name)}
                  />
                </td>
              )}
            </tr>
          ))}
          {locations.length === 0 && (
            <tr>
              <td colSpan={isAdmin ? 2 : 1}>등록된 위치가 없습니다.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
